"use client";

import { useEffect, useState } from "react";
import SearchSummaryBar from "./SearchSummaryBar";
import SearchSummaryMobile from "./SearchSummaryMobile";
import { AMENITIES } from "@/lib/amenities";

/**
 * Sticky compact-search bar + Airbnb-style "Filters" modal. The
 * modal is a centred dialog with sections:
 *   - Recommended for you (quick-toggle icon tiles)
 *   - Type of place (segmented: Any / Room / Entire home)
 *   - Price range (min/max input pills)
 *   - Rooms and beds (stepper rows)
 *   - Amenities (chip grid)
 *
 * Bottom action bar: Clear all link + black "Show N places" submit.
 * Where / When / Guests live ONLY in the sticky bar above; the modal
 * carries them forward via hidden inputs.
 */
type ParamsLike = {
  city?: string;
  checkIn?: string;
  checkOut?: string;
  guests?: string;
  adults?: string;
  children?: string;
  infants?: string;
  minPrice?: string;
  maxPrice?: string;
  radiusKm?: string;
  propertyType?: string;
  roomType?: string;
  beds?: string;
  bedrooms?: string;
  bathrooms?: string;
  minNights?: string;
  pets?: string;
  freeCancel?: string;
  instantBook?: string;
  amenities?: string | string[];
  sort?: string;
};

const PROPERTY_TYPES = ["apartment", "house", "villa", "studio", "cottage", "loft", "townhouse"];

/** Quick-toggle tiles shown in the "Recommended for you" strip. Each
 *  maps to a single amenity / boolean filter. */
const RECOMMENDED: Array<{
  key: string;
  label: string;
  icon: React.ReactNode;
  param: "instantBook" | "freeCancel" | "pets" | "amenities";
  amenityKey?: string;
}> = [
  {
    key: "freeCancel",
    label: "Free cancellation",
    param: "freeCancel",
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4}>
        <circle cx="16" cy="16" r="11" />
        <path d="M11 16l4 4 7-8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    key: "washer",
    label: "Washing machine",
    param: "amenities",
    amenityKey: "washer",
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4}>
        <rect x="6" y="5" width="20" height="22" rx="2" />
        <circle cx="16" cy="18" r="6" />
        <circle cx="22" cy="9" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    key: "self_checkin",
    label: "Self check-in",
    param: "amenities",
    amenityKey: "self_checkin",
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4}>
        <circle cx="11" cy="16" r="4" />
        <path d="M15 16h12M22 12v8M27 12v8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    key: "ac",
    label: "Air conditioning",
    param: "amenities",
    amenityKey: "ac",
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4}>
        <path d="M16 4v24M4 16h24M7 7l18 18M25 7L7 25" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    key: "pool",
    label: "Pool",
    param: "amenities",
    amenityKey: "pool",
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4}>
        <path d="M4 24c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2M8 22V8M24 22V8M8 12h16" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    key: "pets",
    label: "Pets allowed",
    param: "pets",
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4}>
        <circle cx="11" cy="11" r="2" />
        <circle cx="21" cy="11" r="2" />
        <circle cx="8" cy="17" r="2" />
        <circle cx="24" cy="17" r="2" />
        <path d="M16 16c-4 0-7 4-7 7s3 4 7 4 7-1 7-4-3-7-7-7z" />
      </svg>
    ),
  },
];

type Props = {
  params: ParamsLike;
  filterCount: number;
  extraParamsForSummary: Record<string, string | string[]>;
};

export default function SearchFilterPanel({
  params,
  filterCount,
  extraParamsForSummary,
}: Props) {
  const [open, setOpen] = useState(false);

  // ── Local form state — initialised from URL params ─────────
  const requestedAmenities = ([] as string[]).concat(params.amenities ?? []);
  const [amenities, setAmenities] = useState<Set<string>>(new Set(requestedAmenities));
  const [propertyType, setPropertyType] = useState(params.propertyType ?? "");
  const [roomType, setRoomType] = useState(params.roomType ?? "");
  const [minPrice, setMinPrice] = useState(params.minPrice ?? "");
  const [maxPrice, setMaxPrice] = useState(params.maxPrice ?? "");
  const [bedrooms, setBedrooms] = useState<number | null>(
    params.bedrooms ? Math.max(0, Number(params.bedrooms)) : null,
  );
  const [beds, setBeds] = useState<number | null>(
    params.beds ? Math.max(0, Number(params.beds)) : null,
  );
  const [bathrooms, setBathrooms] = useState<number | null>(
    params.bathrooms ? Math.max(0, Number(params.bathrooms)) : null,
  );
  const [pets, setPets] = useState(params.pets === "1" || params.pets === "on");
  const [freeCancel, setFreeCancel] = useState(
    params.freeCancel === "1" || params.freeCancel === "on",
  );
  const [instantBook, setInstantBook] = useState(
    params.instantBook === "1" || params.instantBook === "on",
  );

  // Esc closes modal + body scroll lock while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function toggleAmenity(key: string) {
    setAmenities((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function clearAll() {
    setAmenities(new Set());
    setPropertyType("");
    setRoomType("");
    setMinPrice("");
    setMaxPrice("");
    setBedrooms(null);
    setBeds(null);
    setBathrooms(null);
    setPets(false);
    setFreeCancel(false);
    setInstantBook(false);
  }

  // Sum of all toggled filters on the open modal (live, not URL count).
  const liveCount =
    (propertyType ? 1 : 0) +
    (roomType ? 1 : 0) +
    (minPrice ? 1 : 0) +
    (maxPrice ? 1 : 0) +
    (bedrooms != null ? 1 : 0) +
    (beds != null ? 1 : 0) +
    (bathrooms != null ? 1 : 0) +
    (pets ? 1 : 0) +
    (freeCancel ? 1 : 0) +
    (instantBook ? 1 : 0) +
    amenities.size;

  return (
    <>
      <div className="search-summary">
        <div className="wrap search-summary-desktop">
          <SearchSummaryBar
            initialCity={params.city ?? ""}
            initialCheckIn={params.checkIn ?? ""}
            initialCheckOut={params.checkOut ?? ""}
            initialGuests={params.guests ?? ""}
            initialAdults={params.adults ?? ""}
            initialChildren={params.children ?? ""}
            initialInfants={params.infants ?? ""}
            initialPets={params.pets ?? ""}
            extraParams={extraParamsForSummary}
            filterCount={filterCount}
            onToggleFilters={() => setOpen(true)}
          />
        </div>
        {/* Mobile-only: collapses to a back / pill / filter row. The
            pill opens the full-screen Where/When/Who modal; the filter
            icon opens the Filters modal via onToggleFilters. */}
        <div className="search-summary-mobile">
          <SearchSummaryMobile
            initialCity={params.city ?? ""}
            initialCheckIn={params.checkIn ?? ""}
            initialCheckOut={params.checkOut ?? ""}
            initialAdults={params.adults ?? ""}
            initialChildren={params.children ?? ""}
            initialInfants={params.infants ?? ""}
            initialPets={params.pets ?? ""}
            extraParams={extraParamsForSummary}
            filterCount={filterCount}
            onToggleFilters={() => setOpen(true)}
          />
        </div>
      </div>

      {open ? (
        <>
          <button
            type="button"
            className="filter-modal-scrim"
            aria-label="Close"
            onClick={() => setOpen(false)}
          />
          <div className="filter-modal" role="dialog" aria-modal="true" aria-label="Filters">
            <header className="filter-modal-head">
              <span style={{ width: 36 }} />
              <h2>Filters</h2>
              <button
                type="button"
                aria-label="Close filters"
                className="filter-modal-close"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </header>

            <form
              id="search-filters"
              method="get"
              action="/search"
              className="filter-modal-body"
            >
              {/* Carry where/when/guests + sort forward. */}
              <input type="hidden" name="city" value={params.city ?? ""} />
              <input type="hidden" name="checkIn" value={params.checkIn ?? ""} />
              <input type="hidden" name="checkOut" value={params.checkOut ?? ""} />
              <input type="hidden" name="guests" value={params.guests ?? ""} />
              {params.sort ? <input type="hidden" name="sort" value={params.sort} /> : null}

              {/* Quick toggle: collect amenity tile selections as
                  <input name="amenities" value="…"> hidden fields,
                  rendered from state, so the form posts the right
                  set on submit. */}
              {[...amenities].map((a) => (
                <input key={a} type="hidden" name="amenities" value={a} />
              ))}
              {pets ? <input type="hidden" name="pets" value="1" /> : null}
              {freeCancel ? <input type="hidden" name="freeCancel" value="1" /> : null}
              {instantBook ? <input type="hidden" name="instantBook" value="1" /> : null}
              {propertyType ? <input type="hidden" name="propertyType" value={propertyType} /> : null}
              {roomType ? <input type="hidden" name="roomType" value={roomType} /> : null}
              {minPrice ? <input type="hidden" name="minPrice" value={minPrice} /> : null}
              {maxPrice ? <input type="hidden" name="maxPrice" value={maxPrice} /> : null}
              {bedrooms != null ? <input type="hidden" name="bedrooms" value={bedrooms} /> : null}
              {beds != null ? <input type="hidden" name="beds" value={beds} /> : null}
              {bathrooms != null ? <input type="hidden" name="bathrooms" value={bathrooms} /> : null}

              {/* ── Recommended for you ────────────────────── */}
              <section className="filter-section">
                <h3 className="filter-section-title">Recommended for you</h3>
                <div className="filter-tile-row">
                  {RECOMMENDED.map((r) => {
                    const isOn = (() => {
                      if (r.param === "amenities" && r.amenityKey) return amenities.has(r.amenityKey);
                      if (r.param === "pets") return pets;
                      if (r.param === "freeCancel") return freeCancel;
                      if (r.param === "instantBook") return instantBook;
                      return false;
                    })();
                    return (
                      <button
                        key={r.key}
                        type="button"
                        className={`filter-tile${isOn ? " on" : ""}`}
                        onClick={() => {
                          if (r.param === "amenities" && r.amenityKey) toggleAmenity(r.amenityKey);
                          else if (r.param === "pets") setPets((v) => !v);
                          else if (r.param === "freeCancel") setFreeCancel((v) => !v);
                          else if (r.param === "instantBook") setInstantBook((v) => !v);
                        }}
                      >
                        <span className="filter-tile-icon">{r.icon}</span>
                        <span className="filter-tile-label">{r.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* ── Type of place ─────────────────────────── */}
              <section className="filter-section">
                <h3 className="filter-section-title">Type of place</h3>
                <div className="filter-seg" role="group">
                  {[
                    { value: "", label: "Any type" },
                    { value: "private", label: "Room" },
                    { value: "entire", label: "Entire home" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`filter-seg-btn${roomType === opt.value ? " on" : ""}`}
                      onClick={() => setRoomType(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </section>

              {/* ── Price range ───────────────────────────── */}
              <section className="filter-section">
                <h3 className="filter-section-title">Price range</h3>
                <p className="filter-section-sub">Per night · in your selected currency</p>
                <div className="filter-price-row">
                  <label className="filter-price-pill">
                    <span>Minimum</span>
                    <div className="filter-price-input">
                      <span>€</span>
                      <input
                        type="number"
                        min={0}
                        placeholder="0"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                      />
                    </div>
                  </label>
                  <label className="filter-price-pill">
                    <span>Maximum</span>
                    <div className="filter-price-input">
                      <span>€</span>
                      <input
                        type="number"
                        min={0}
                        placeholder="Any"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                      />
                    </div>
                  </label>
                </div>
              </section>

              {/* ── Rooms and beds ────────────────────────── */}
              <section className="filter-section">
                <h3 className="filter-section-title">Rooms and beds</h3>
                <RoomStepper label="Bedrooms" value={bedrooms} onChange={setBedrooms} />
                <RoomStepper label="Beds" value={beds} onChange={setBeds} />
                <RoomStepper label="Bathrooms" value={bathrooms} onChange={setBathrooms} step={0.5} last />
              </section>

              {/* ── Property type (still useful for searches) ── */}
              <section className="filter-section">
                <h3 className="filter-section-title">Property type</h3>
                <div className="filter-chip-grid">
                  <button
                    type="button"
                    className={`filter-chip${propertyType === "" ? " on" : ""}`}
                    onClick={() => setPropertyType("")}
                  >
                    Any
                  </button>
                  {PROPERTY_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`filter-chip${propertyType === t ? " on" : ""}`}
                      onClick={() => setPropertyType(t)}
                    >
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>
              </section>

              {/* ── Amenities ─────────────────────────────── */}
              <section className="filter-section">
                <h3 className="filter-section-title">Amenities</h3>
                <div className="filter-chip-grid">
                  {AMENITIES.map((a) => (
                    <button
                      key={a.key}
                      type="button"
                      className={`filter-chip${amenities.has(a.key) ? " on" : ""}`}
                      onClick={() => toggleAmenity(a.key)}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              </section>
            </form>

            <footer className="filter-modal-foot">
              <button type="button" className="filter-clear-link" onClick={clearAll}>
                Clear all
              </button>
              <button type="submit" form="search-filters" className="filter-modal-submit">
                Show {liveCount > 0 ? `results · ${liveCount} filter${liveCount === 1 ? "" : "s"}` : "all results"}
              </button>
            </footer>
          </div>
        </>
      ) : null}
    </>
  );
}

function RoomStepper({
  label,
  value,
  onChange,
  step = 1,
  last,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  step?: number;
  last?: boolean;
}) {
  const display = value == null ? "Any" : value === 0 ? "Any" : `${value}+`;
  const canDec = value != null && value > 0;
  return (
    <div className={`filter-stepper-row${last ? " last" : ""}`}>
      <div className="filter-stepper-label">{label}</div>
      <div className="filter-stepper">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          disabled={!canDec}
          onClick={() => {
            if (value == null) return;
            const next = Math.max(0, value - step);
            onChange(next === 0 ? null : next);
          }}
        >
          −
        </button>
        <span>{display}</span>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          onClick={() => onChange((value ?? 0) + step)}
        >
          +
        </button>
      </div>
    </div>
  );
}
